import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import fetchWithTenant from '../utils/fetchWithTenant';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Home, Video, Archive, Shield, Phone, Settings, ClipboardCheck, FileText, LogOut, Menu, X, PanelLeftClose, PanelLeft, Bell,
  Monitor, Activity, Building2, Eye, MapPin, ChevronDown, ChevronRight, ClipboardList, Users, Sun, User, KeyRound, Radio, Heart, AlertTriangle, Bot, FileBarChart
, Network, Tablet, Package, Link2, Boxes} from 'lucide-react';
import useAuthStore from '../stores/authStore';
import TenantSelector from './TenantSelector';
import { useBranding } from '../contexts/BrandingContext';

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [isTempExpanded, setIsTempExpanded] = useState(false);
  const [showSitePicker, setShowSitePicker] = useState(false);
  const longPressTimer = React.useRef(null);

  // Effective collapsed state - collapsed unless temporarily expanded
  const effectiveCollapsed = isCollapsed && !isTempExpanded;

  const [expandedSections, setExpandedSections] = useState(['video', 'access', 'monitor', 'entry']);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const toggleCollapsed = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', String(newState));
  };
  const { user, logout, isAdmin, isManager, isOperator, isPlatformAdmin, tenants, currentTenant, switchTenant, hasTenantPermission } = useAuthStore();
  const navigate = useNavigate();
  const { branding, refreshBranding } = useBranding();
  // Tenant-level logo (fallback when org branding is null)
  const [tenantLogoUrl, setTenantLogoUrl] = useState(null);

  // ── Derive module & integration flags from tenant context ──
  const modules = currentTenant?.modules || {};
  const hasAcs          = modules.hasAcs || false;
  const hasVms          = modules.hasVms || false;
  const hasFr           = modules.hasFr || false;
  const hasShabbatConfig = modules.hasShabbatConfig || false;
  const shabbatEnabled   = modules.shabbatEnabled || false;
  const hasMdLicense    = modules.hasMdLicense || false;
  const hasEntryLicense = modules.hasEntryLicense || false;
  const hasRaLicense    = modules.hasRaLicense || false;

  // Permission flags (from UserTenantAccess)
  const canViewMD    = hasTenantPermission('canViewMD');
  const canViewRA    = hasTenantPermission('canViewRA');
  const canViewEntry = hasTenantPermission('canViewEntry');
  const canViewInvestigations = hasTenantPermission('canViewInvestigations');

  // Role checks (call once for stable values in render + effects)
  const adminRole   = isAdmin();
  const managerRole = isManager();

  useEffect(() => {
    fetchWithTenant('/api/global-config')
      .then(res => res.json())
      .then(config => {
        if (config.logoUrl) setTenantLogoUrl(config.logoUrl);
      })
      .catch(() => {});
  }, [currentTenant?.id]);

  // Platform bug count polling
  const [platformBugCount, setPlatformBugCount] = useState(0);

  const fetchPlatformBugCount = useCallback(async () => {
    try {
      const res = await fetch('/api/platform/bugs/stats', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPlatformBugCount(data.open || 0);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (user?.isPlatformAdmin) {
      fetchPlatformBugCount();
      const interval = setInterval(fetchPlatformBugCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchPlatformBugCount]);

  // Notification badge polling
  const [notificationCount, setNotificationCount] = useState(0);

  const fetchNotificationCount = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/notifications/stats');
      if (res.ok) {
        const data = await res.json();
        setNotificationCount(data.unresolved || 0);
      }
    } catch (e) {
      // Silently fail - badge is non-critical
    }
  }, []);

  useEffect(() => {
    if (user && adminRole) {
      fetchNotificationCount();
      const interval = setInterval(fetchNotificationCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, adminRole, fetchNotificationCount, currentTenant?.id]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // ─── NAV STRUCTURE ─────────────────────────────────────────
  // Visibility rules:
  //   Registration  = tenant has ACS + user is ADMIN or MANAGER
  //   Sabbath Mode  = tenant has ShabbatConfig enabled + user has canActivateShabbat
  //   CCM Monitor   = user has canViewMD + tenant has MdLicense
  //   Rec Assurance = tenant has RaLicense + user has canViewMD
  //   Entry         = user has canViewEntry + tenant has EntryLicense
  //   Reports       = ADMIN or MANAGER
  //   Notifications = ADMIN only
  //   Settings      = ADMIN only
  //   Lockdown      = canLockdown (handled on Home page, not sidebar)
  const navSections = [
    {
      id: 'home',
      type: 'link',
      to: '/',
      icon: Home,
      label: 'Home',
      show: true
    },
    {
      id: 'video',
      type: 'section',
      icon: Video,
      label: 'Video',
      show: true,
      children: [
        { to: '/cameras', icon: Video, label: 'Cameras', show: true },
        { to: '#videowall', icon: Monitor, label: 'Video Wall', show: true, isVideoWall: true },
        { to: '/intercoms', icon: Phone, label: 'Intercoms', show: user?.canAnswerIntercom },
        { to: '/analytics', icon: Activity, label: 'Analytics', show: adminRole },
      ]
    },
    {
      id: 'access',
      type: 'section',
      icon: Shield,
      label: 'Access Control',
      show: hasAcs,
      children: [
        { to: '/access', icon: Shield, label: 'Door Control', show: true },
        { to: '/registration', icon: ClipboardList, label: 'Registration', show: managerRole },
        { to: '/shabbat', icon: Sun, label: 'Sabbath Mode', show: hasShabbatConfig && (user?.isPlatformAdmin || (shabbatEnabled && user?.canActivateShabbat)) },
      ]
    },
    {
      id: 'map',
      type: 'link',
      to: '/map',
      icon: MapPin,
      label: 'Site Map',
      show: true
    },

    // ── Separator: Modules ──
    { id: 'sep-modules', type: 'separator' },

    {
      id: 'monitor',
      type: 'section',
      icon: Activity,
      label: 'CCM Monitor',
      show: canViewMD && hasMdLicense,
      children: [
        { to: '/monitoring', icon: Activity, label: 'Dashboard', show: true },
        { to: '/monitoring/devices', icon: Monitor, label: 'Devices', show: true },
        { to: '/monitoring/onwatch', icon: Boxes, label: 'OnWatch', show: adminRole },
        { to: '/monitoring/operations', icon: Settings, label: 'Settings', show: adminRole },
        { to: '/recording-assurance', icon: Shield, label: 'Recording Assurance', show: canViewRA && hasRaLicense },
      ]
    },
    {
      id: 'entry',
      type: 'section',
      icon: Tablet,
      label: branding.isWhiteLabeled ? 'Entry' : 'Serverli Entry',
      show: canViewEntry && hasEntryLicense,
      children: [
        { to: '/entry', icon: Tablet, label: 'Devices', show: true },
        { to: '/entry/profiles', icon: Users, label: 'Profiles', show: true },
        { to: '/settings/entry/apks', icon: Package, label: 'APK Updates', show: user?.isPlatformAdmin },
      ]
    },

    // ── Separator: System ──
    { id: 'sep-system', type: 'separator' },

    {
      id: 'notifications',
      type: 'link',
      to: '/notifications',
      icon: Bell,
      label: 'Notifications',
      show: adminRole,
      badge: notificationCount > 0 ? notificationCount : null,
      badgeType: 'alert',
    },
    {
      id: 'investigations',
      type: 'section',
      label: 'Investigations',
      icon: ClipboardList,
      show: adminRole || canViewInvestigations,
      children: [
        { to: '/investigations', icon: Activity, label: 'Events', show: adminRole || canViewInvestigations },
        { to: '/investigations/associations', icon: Link2, label: 'Associations', show: adminRole || canViewInvestigations },
        { to: '/reports', icon: FileBarChart, label: 'Reports', show: adminRole || managerRole },
      ],
    },
    {
      id: 'site-settings',
      type: 'link',
      to: '/admin',
      icon: Settings,
      label: 'Settings',
      show: adminRole,
    },

    // ── Separator: Platform (staff only) ──
    { id: 'sep-platform', type: 'separator', show: user?.isPlatformAdmin },

    {
      id: 'platform',
      type: 'link',
      to: '/platform',
      icon: Building2,
      label: 'Platform',
      show: user?.isPlatformAdmin,
    },
    {
      id: 'server-health',
      type: 'link',
      to: '/server-health',
      icon: Activity,
      label: 'Server Health',
      show: user?.isPlatformAdmin,
    },
    { id: 'sep-compliance', type: 'separator', show: user?.isPlatformAdmin },
    {
      id: 'access-review',
      type: 'link',
      to: '/access-review',
      icon: ClipboardCheck,
      label: 'Access Review',
      show: user?.isPlatformAdmin,
    },
    {
      id: 'audit-log',
      type: 'link',
      to: '/audit-log',
      icon: FileText,
      label: 'Audit Log',
      show: user?.isPlatformAdmin,
    },
  ];

  return (
    <>
      <button
        className="mobile-menu-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle menu"
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
      <aside className={`sidebar ${isOpen ? 'open' : ''} ${effectiveCollapsed ? 'collapsed' : ''}`} onMouseLeave={() => setIsTempExpanded(false)}>
        <div className="sidebar-header">
          <img
            src={branding.brandLogoUrl || tenantLogoUrl || '/serverliccm-logo.png'}
            alt={branding.brandName}
            className="sidebar-logo"
          />
        </div>

        {/* Tenant Selector */}
        {tenants.length > 1 && (
          <div className="sidebar-tenant">
            <TenantSelector />
          </div>
        )}
        {tenants.length === 1 && currentTenant && (
          <div className="sidebar-tenant-single">
            <Building2 size={14} />
            <span>{currentTenant.name}</span>
          </div>
        )}

        <nav>
          {navSections.map(item => {
            // Separator
            if (item.type === 'separator') {
              if (item.show === false) return null;
              return <div key={item.id} className="nav-separator" />;
            }

            if (!item.show) return null;

            // Simple link
            if (item.type === 'link') {
              const isHomeItem = item.id === 'home' && tenants.length > 1;
              return (
                <React.Fragment key={item.id}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''} ${item.id === 'platform' ? 'nav-platform' : ''}`}
                    title={effectiveCollapsed ? item.label : isHomeItem ? 'Hold to switch site' : ''}
                    onClick={(e) => {
                      if (isCollapsed && !isTempExpanded) {
                        e.preventDefault();
                        setIsTempExpanded(true);
                      } else {
                        setIsOpen(false);
                      }
                    }}
                    onTouchStart={(e) => {
                      if (!isHomeItem) return;
                      longPressTimer.current = setTimeout(() => {
                        e.preventDefault();
                        setShowSitePicker(true);
                      }, 500);
                    }}
                    onTouchEnd={() => {
                      if (longPressTimer.current) {
                        clearTimeout(longPressTimer.current);
                        longPressTimer.current = null;
                      }
                    }}
                    onTouchMove={() => {
                      if (longPressTimer.current) {
                        clearTimeout(longPressTimer.current);
                        longPressTimer.current = null;
                      }
                    }}
                  >
                    <item.icon size={20} />
                    <span>{item.label}</span>
                    {item.badge && !effectiveCollapsed && (
                      <span className={`nav-badge ${item.badgeType === 'alert' ? 'alert-badge' : 'god-mode'} `}>{item.badge > 99 ? '99+' : item.badge}</span>
                    )}
                  </NavLink>
                </React.Fragment>
              );
            }

            // Collapsible section
            if (item.type === 'section') {
              const isExpanded = expandedSections.includes(item.id);
              const visibleChildren = item.children.filter(c => c.show);
              if (visibleChildren.length === 0) return null;

              return (
                <div key={item.id} className="nav-section">
                  <button
                    className="nav-section-header"
                    title={effectiveCollapsed ? item.label : ''}
                    onClick={() => {
                      if (isCollapsed && !isTempExpanded) {
                        setIsTempExpanded(true);
                      } else {
                        toggleSection(item.id);
                      }
                    }}
                  >
                    <item.icon size={20} />
                    <span>{item.label}</span>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  {isExpanded && (
                    <div className="nav-section-items">
                      {visibleChildren.map(child => (
                        child.isVideoWall ? (
                          <button
                            key={child.to}
                            className="nav-link nav-child"
                            onClick={(e) => {
                              e.preventDefault();
                              setIsOpen(false);
                              window.open('/video-wall', 'serverli-videowall', 'width=1920,height=1080,menubar=no,toolbar=no,location=no,status=no');
                            }}
                          >
                            <child.icon size={18} />
                            <span>{child.label}</span>
                          </button>
                        ) : (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            end={child.to === '/monitoring' || child.to === '/entry'}
                            className={({ isActive }) => `nav-link nav-child ${isActive ? 'active' : ''}`}
                            onClick={() => setIsOpen(false)}
                          >
                            <child.icon size={18} />
                            <span>{child.label}</span>
                          </NavLink>
                        )
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
        </nav>

        <button
          className="sidebar-collapse-btn"
          onClick={toggleCollapsed}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
        </button>
        <div className="sidebar-footer" style={{ position: 'relative' }}>
          {/* User Menu Dropdown */}
          {showUserMenu && (
            <div
              className="user-menu-dropdown"
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '8px',
                right: '8px',
                marginBottom: '8px',
                backgroundColor: 'var(--bg-sidebar)',
                border: '0.5px solid rgba(255,255,255,0.15)',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.4)',
                zIndex: 1000
              }}
            >
              <NavLink
                to="/profile"
                onClick={() => setShowUserMenu(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  color: 'rgba(255,255,255,0.85)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  borderBottom: '0.5px solid rgba(255,255,255,0.1)',
                  transition: 'background-color 0.15s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <User size={18} />
                Account Settings
              </NavLink>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  handleLogout();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  color: '#f87171',
                  backgroundColor: 'transparent',
                  border: 'none',
                  width: '100%',
                  textAlign: 'left',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <LogOut size={18} />
                Sign Out
              </button>
            </div>
          )}

          {/* Clickable User Info */}
          <button
            className="user-info-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 12px',
              backgroundColor: showUserMenu ? '#2a2a2a' : 'transparent',
              border: '1px solid transparent',
              borderRadius: '8px',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
              transition: 'all 0.15s'
            }}
            onMouseOver={(e) => {
              if (!showUserMenu) e.currentTarget.style.backgroundColor = '#1e1e1e';
            }}
            onMouseOut={(e) => {
              if (!showUserMenu) e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {/* Avatar */}
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: '600',
              color: '#fff',
              flexShrink: 0
            }}>
              {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
            </div>

            {/* User Info - hidden when collapsed */}
            {!effectiveCollapsed && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: 'var(--bg-sidebar-text-hover)',
                  fontSize: '14px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {user?.name || user?.username}
                </div>
                <div style={{
                  color: 'var(--bg-sidebar-text)',
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {user?.isPlatformAdmin ? 'Platform Admin' : (currentTenant?.role || user?.role)}
                </div>
              </div>
            )}

            {/* Chevron - hidden when collapsed */}
            {!effectiveCollapsed && (
              <ChevronDown
                size={16}
                style={{
                  color: 'var(--bg-sidebar-text)',
                  transform: showUserMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              />
            )}
          </button>
        </div>
      </aside>
      {isOpen && <div className="sidebar-overlay" onClick={() => setIsOpen(false)} />}
    <>
      {/* Site Picker Sheet - long press home on mobile */}
      {showSitePicker && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
          }}
          onClick={() => setShowSitePicker(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#1a2236',
              borderRadius: '16px 16px 0 0',
              padding: '0 0 calc(1.5rem + env(safe-area-inset-bottom))',
              width: '100%',
              maxWidth: '480px',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            {/* Handle */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
              <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.2)' }} />
            </div>
            <div style={{ padding: '0 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>Switch Site</p>
            </div>
            <div style={{ padding: '8px 0' }}>
              {tenants.map(tenant => (
                <button
                  key={tenant.id}
                  onClick={async () => {
                    if (switchTenant) {
                      await switchTenant(tenant.id);
                      const orgId = useAuthStore.getState().currentTenant?.organizationId;
                      if (orgId) {
                        try { await refreshBranding(orgId); } catch {}
                      }
                    }
                    setShowSitePicker(false);
                    setIsOpen(false);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    width: '100%', padding: '14px 20px',
                    background: tenant.id === currentTenant?.id ? 'rgba(0,212,170,0.08)' : 'none',
                    border: 'none', cursor: 'pointer', textAlign: 'left',
                    borderLeft: tenant.id === currentTenant?.id ? '3px solid #00d4aa' : '3px solid transparent'
                  }}
                >
                  <Building2 size={18} color={tenant.id === currentTenant?.id ? '#00d4aa' : '#94a3b8'} />
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '500', color: tenant.id === currentTenant?.id ? '#00d4aa' : '#e2e8f0' }}>
                      {tenant.name}
                    </div>
                    {tenant.id === currentTenant?.id && (
                      <div style={{ fontSize: '11px', color: '#00d4aa', marginTop: '2px' }}>Current site</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
    </>
  );
};

export default Sidebar;
